"use client";

import {
  ctaClick,
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
} from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import { ArrowUpRight } from "lucide-react";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  LEAD_DROP_FORM_MOBILE_CTA_VARIANTS,
  LEAD_DROP_FORM_SECTION_VARIANTS,
  LEAD_DROP_FORM_VARIANTS,
  LEAD_DROP_VARIANTS,
} from "../../../constants";
import InlineLeadForm from "../leaddropform/InlineLeadForm";

export interface Enrollments5MetaItem {
  id?: string | number;
  meta_priority?: number | string;
  meta_status?: boolean;
  meta_icon?: string;
  meta_icon_alt?: string;
  meta_field_1?: string;
  meta_field_2?: string;
}

export interface Enrollments5Data {
  id?: string;
  status?: boolean;
  title?: string;
  description?: string;
  cta_label?: string;
  cta_url?: string;
  open_in_new_tab?: boolean;
  background_color?: string;
  title_color?: string;
  title_highlight_color?: string;
  description_color?: string;
  body_text_color?: string;
  accent_color?: string;
  cta_background_color?: string;
  cta_text_color?: string;
  enable_lead_popup?: boolean;
  enable_lead_form?: boolean;
  metadata?: Enrollments5MetaItem[];
}

interface Enrollments5Props {
  data: Enrollments5Data | { data: Enrollments5Data };
}

function resolvePayload(
  data: Enrollments5Props["data"],
): Enrollments5Data | undefined {
  const anyData = data as { data?: Enrollments5Data };
  if (anyData?.data && typeof anyData.data === "object") {
    return anyData.data as Enrollments5Data;
  }
  return data as Enrollments5Data;
}

/**
 * CMS colour columns can hold a half-typed value — the admin colour input saves
 * per keystroke, so `#DB5803` lands as `#D` if the field is left mid-edit. A
 * malformed hex is dropped by the browser, which rendered the CTA with no
 * background at all (invisible on a white section). Destructuring defaults
 * don't cover it: the column is non-empty, just not a colour. Validate the
 * shape here and fall back, which also strips anything that could escape the
 * inline `style=` attribute.
 */
function resolveColor(raw: string | undefined, fallback: string): string {
  const value = (raw ?? "").trim();
  if (!value) return fallback;
  if (value.startsWith("#")) {
    return /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(value)
      ? value
      : fallback;
  }
  // rgb()/hsl()/named colours pass through, minus attribute-escaping chars.
  return /^[a-z0-9(),.%\s-]+$/i.test(value) ? value : fallback;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Wraps `{highlight}` / `{{line}}` segments in a span styled with the supplied
 * highlight color. Mirrors `formatSectionTitle` semantics but applies the color
 * via inline `style=` so admin-driven hex values don't need Tailwind JIT.
 */
function formatTitleWithHighlight(
  title: string,
  highlightColor: string,
): string {
  if (!title) return "";
  const safeColor = highlightColor.replace(/[^#a-zA-Z0-9(),.%\s]/g, "");
  const openTag = `<span style="color:${safeColor}">`;

  if (title.includes("{{")) {
    return title
      .replace(
        /\{\{([^}]+)\}\}/g,
        (_m, inner) => `${openTag}${escapeHtml(inner)}</span><br/>`,
      )
      .replace(/\s+/g, " ");
  }

  if (title.includes("{")) {
    return title
      .replace(/\{/g, openTag)
      .replace(/\}/g, "</span>")
      .replace(/\s+/g, " ");
  }

  return escapeHtml(title);
}

const Enrollments5 = ({ data }: Enrollments5Props) => {
  const dispatch = useDispatch();
  const sectionData = resolvePayload(data);
  const ref = useRef<HTMLElement>(null);

  const {
    status = true,
    title = "About this {Programme}",
    description = "",
    cta_label = "Apply Now",
    cta_url = "",
    open_in_new_tab = true,
    background_color = "#FFFFFF",
    title_color = "#0A0A0A",
    title_highlight_color = "#DB5803",
    description_color = "#0A0A0A",
    body_text_color = "#0A0A0A",
    accent_color = "#DB5803",
    cta_background_color = "#DB5803",
    cta_text_color = "#FFFFFF",
    enable_lead_popup: enableLeadRaw,
    metadata = [],
  } = sectionData || {};

  const raw = sectionData as Record<string, unknown> | undefined;
  const enableLeadPopup = isCmsShowFlagOn(
    raw?.enable_lead_popup ?? raw?.enableLeadPopup ?? enableLeadRaw,
    true,
  );
  const enableLeadForm = isCmsShowFlagOn(
    raw?.enable_lead_form ?? raw?.enableLeadForm,
    false,
  );

  const bgColor = resolveColor(background_color, "#FFFFFF");
  const titleColor = resolveColor(title_color, "#0A0A0A");
  const highlightColor = resolveColor(title_highlight_color, "#DB5803");
  const descriptionColor = resolveColor(description_color, "#0A0A0A");
  const bodyTextColor = resolveColor(body_text_color, "#0A0A0A");
  const accentColor = resolveColor(accent_color, "#DB5803");
  // A blank/malformed CTA background falls through to the page's accent colour
  // before the hard default, so the button stays on-brand for the page it's on.
  const ctaBgColor = resolveColor(cta_background_color, accentColor);
  const ctaTextColor = resolveColor(cta_text_color, "#FFFFFF");

  const hrefTrim = (cta_url || "").trim();
  const ctaOpensLeadPopup = enableLeadPopup || !hrefTrim;
  // Rows seeded before the CTA field existed store `""` (not null), so the
  // destructuring default above never kicks in — fall back here as well.
  const ctaLabel = (cta_label || "").trim() || "Apply Now";

  const sortedPoints = useMemo(() => {
    if (!Array.isArray(metadata) || metadata.length === 0) return [];
    return [...metadata]
      .filter(
        (m) => m && (m.meta_status === undefined || m.meta_status === true),
      )
      .sort(
        (a, b) => Number(a.meta_priority ?? 0) - Number(b.meta_priority ?? 0),
      );
  }, [metadata]);

  const openLeadFromCta = useCallback(() => {
    ctaClick({
      section: "enrollments_5",
      component: "cta_apply",
      label: ctaLabel,
      step: "1",
      entity: "enrollments",
      parentEntity: "enrollments_5",
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
      }),
    );
  }, [ctaLabel, dispatch]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current && status) {
  //     FireWidgetViewedEvent(ref, "enrollments_5");
  //   }
  // }, [status]);

  if (!status) return null;

  const ctaClassName = cn(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md shadow-sm transition-opacity hover:opacity-95",
    // mobile: full-width button matching the design spec
    "h-10 w-full px-8 text-center text-xs font-medium tracking-normal",
    // >=md: shrink to content, left-aligned, larger type
    "md:h-auto md:w-auto md:shrink-0 md:justify-start md:gap-2.5 md:px-5 md:py-3 md:text-base md:font-semibold md:leading-[23px]",
    ctaOpensLeadPopup && "cursor-pointer",
    !ctaOpensLeadPopup && !hrefTrim && "cursor-default opacity-90",
  );

  const ctaIconClass = "size-5 shrink-0 min-[768px]:size-6";
  const ctaStyle = {
    backgroundColor: ctaBgColor,
    color: ctaTextColor,
  };

  const renderCta = () => {
    if (ctaOpensLeadPopup) {
      return (
        <button
          type="button"
          className={ctaClassName}
          style={ctaStyle}
          onClick={openLeadFromCta}
        >
          {ctaLabel}
          <ArrowUpRight className={ctaIconClass} strokeWidth={2} aria-hidden />
        </button>
      );
    }
    if (hrefTrim) {
      return (
        <a
          href={hrefTrim}
          target={open_in_new_tab ? "_blank" : undefined}
          rel={open_in_new_tab ? "noopener noreferrer" : undefined}
          className={ctaClassName}
          style={ctaStyle}
          onClick={() =>
            ctaClick({
              section: "enrollments_5",
              component: "cta_apply_link",
              label: ctaLabel,
              step: "1",
              entity: "enrollments",
              parentEntity: "enrollments_5",
            })
          }
        >
          {ctaLabel}
          <ArrowUpRight className={ctaIconClass} strokeWidth={2} aria-hidden />
        </a>
      );
    }
    return (
      <span
        className={cn(ctaClassName, "cursor-default opacity-90")}
        style={ctaStyle}
      >
        {ctaLabel}
        <ArrowUpRight className={ctaIconClass} strokeWidth={2} aria-hidden />
      </span>
    );
  };

  return (
    <section
      ref={ref}
      data-enrollments="5"
      className="flex w-full items-center justify-center"
      style={{ backgroundColor: bgColor }}
    >
      <div
        className={cn(
          "max-w-screen-container mx-auto h-fit w-full px-4 py-[22px] pt-[44px]",
          "flex flex-col gap-8",
          "min-[1024px]:px-15 min-[1024px]:py-10 min-[1024px]:pt-10",
          enableLeadForm &&
            "min-[1024px]:flex-row min-[1024px]:items-start min-[1024px]:gap-12 min-[1280px]:gap-16",
        )}
      >
        <div className="flex flex-1 flex-col items-start gap-6">
          <div className="flex w-full flex-col gap-3">
            {title ? (
              <h2
                className="max-w-full text-[28px] font-semibold leading-tight tracking-[-0.03em]"
                style={{
                  color: titleColor,
                  fontFamily: "var(--font-poppins), Poppins, sans-serif",
                }}
                dangerouslySetInnerHTML={{
                  __html: formatTitleWithHighlight(title, highlightColor),
                }}
              />
            ) : null}

            {description ? (
              <p
                className="whitespace-pre-wrap text-base"
                style={{ color: descriptionColor }}
              >
                {description}
              </p>
            ) : null}

            {sortedPoints.length > 0 ? (
              <div
                className={cn(
                  "mt-6",
                  enableLeadForm
                    ? "flex flex-col gap-y-8"
                    : "grid grid-cols-1 gap-x-6 gap-y-8 min-[640px]:grid-cols-2 min-[1024px]:grid-cols-3 min-[1024px]:gap-y-10",
                )}
              >
                {sortedPoints.map((keyPoint, index) => (
                  <div
                    className="flex h-auto flex-row items-start gap-3"
                    key={keyPoint.id ?? `point-${index}`}
                  >
                    {keyPoint.meta_icon ? (
                      <Image
                        className="h-[42px] min-h-[42px] w-[42px] min-w-[42px] shrink-0 object-contain"
                        src={keyPoint.meta_icon}
                        alt={
                          keyPoint.meta_icon_alt || keyPoint.meta_field_1 || ""
                        }
                        width={42}
                        height={42}
                      />
                    ) : null}
                    <div className="flex min-w-0 flex-1 flex-col">
                      {keyPoint.meta_field_1 ? (
                        <div
                          className="font-sans text-[14px] font-normal not-italic leading-none"
                          style={{ color: bodyTextColor }}
                        >
                          {keyPoint.meta_field_1}
                        </div>
                      ) : null}
                      {keyPoint.meta_field_2 ? (
                        <div
                          className="font-sans text-base font-bold not-italic leading-[26px]"
                          style={{ color: accentColor }}
                        >
                          {keyPoint.meta_field_2}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        {/* Bottom CTA — mobile only (<800px), per design. From 800px up it's
            hidden regardless of `enable_lead_form`: with the form on, the form
            itself carries the submit action; with it off, the page header /
            sticky CTA does. Note `md:` here resolves to 1230px (custom theme
            scale in globals.css), so the breakpoint must be an arbitrary
            `min-[...]` variant. */}
        <div className="flex w-full min-[800px]:hidden">{renderCta()}</div>

        {/* Right column — inline lead form (>=800px). Everything here is driven
            by CMS data + CSS, never by JS state, so server, hydration and
            post-hydration render the same tree: the shell ships with the page and
            the card streams into it when its `ssr: false` chunk resolves (Banner9
            does the same). No loading state in between — the empty shell holds
            the layout. `InlineLeadForm` is mounted unconditionally on purpose: an
            `ssr: false` child carries a Suspense boundary that the server writes
            markers for, so gating it on the store-driven variant would make the
            boundary exist only on the client — a hydration mismatch even though
            it renders nothing. The variant lookup lives inside it instead. */}
        {enableLeadForm ? (
          <div
            className={cn(
              "hidden w-full justify-center overflow-hidden min-[800px]:flex",
              // The column HUGS the form card instead of reserving a fixed
              // 475/560px slot: every form variant caps its own width (form 10
              // is 360/364/400px), so a wider column left ~160px of dead space
              // between the copy and the card — the "big desktop gap". `w-fit`
              // keeps the card flush with the container's right edge and hands
              // the freed width back to the `flex-1` copy column, matching the
              // reference LP. From 1080px (`sm`) up every variant has a
              // definite width, so fit-content resolves to the card; below that
              // the cards carry no width of their own, hence the explicit
              // 360px. max-w keeps a `w-full` variant from eating the row.
              "min-[1024px]:w-[360px] min-[1024px]:shrink-0 min-[1024px]:justify-end",
              "min-[1080px]:w-fit min-[1080px]:max-w-[560px]",
              // While the shell is still empty, `:empty` (higher specificity, so
              // it wins over `w-fit` regardless of order) keeps the reserved
              // width so the copy column never re-wraps when the card lands...
              "min-[1080px]:empty:w-[360px]",
              // ...but below 1024px the column stacks UNDER the copy, where a
              // reserved band would just be a blank gap — collapse it instead
              // until there is a card to show.
              "max-[1024px]:empty:hidden",
            )}
          >
            <InlineLeadForm />
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default Enrollments5;
